import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Badge } from "@/components/ui/badge";
import VerificationBadge from "@/components/VerificationBadge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { MapPin, Wrench, Send, Globe, Facebook, Instagram, Phone, Flag, Lock } from "lucide-react";
import ArtisanBadges, { ArtisanSignals } from "@/components/ArtisanBadges";
import PortfolioGallery from "@/components/PortfolioGallery";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, useParams } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { ReportModal } from "@/components/ReportModal";
import { useHandymanSpecialties } from "@/hooks/useHandymanSpecialties";
import { getHandymanLabel, type HandymanSpecialtyCode } from "@/lib/handymanConfig";
import { useSEO, useJsonLd } from "@/hooks/useSEO";
import { useLanguage } from "@/hooks/useLanguage";
import { useLocalizedNavigate } from "@/hooks/useLocalizedNavigate";
import { getCategoryColorsByLabel } from "@/lib/categoryColors";
import { cn } from "@/lib/utils";

const ProfilArtisan = () => {
  const { id } = useParams<{ id: string }>();
  const { t, locale } = useLanguage("artisan");
  const lNavigate = useLocalizedNavigate();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);

  const { data: artisan, isLoading } = useQuery({
    queryKey: ["artisan-public", id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("artisan_profiles")
        .select("*")
        .eq("user_id", id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const { data: profile } = useQuery({
    queryKey: ["profile-public", id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("display_name, avatar_url")
        .eq("user_id", id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!id,
  });

  const displayName = profile?.display_name || artisan?.raison_sociale || "Artisan";

  useSEO({
    title: artisan
      ? `${displayName} — ${artisan.specialite} à ${artisan.commune}, ${artisan.wilaya}`
      : t("profil_public.not_found"),
    description: artisan?.description
      ? artisan.description.slice(0, 155)
      : `Contactez ${displayName}, ${artisan?.specialite ?? "artisan"} à ${artisan?.commune ?? ""} sur Hirafi.`,
    canonical: id ? `/artisan/${id}` : undefined,
    locale,
  });

  useJsonLd(
    artisan
      ? {
          "@context": "https://schema.org",
          "@type": "Person",
          name: displayName,
          jobTitle: artisan.specialite,
          description: artisan.description ?? undefined,
          url: `https://hirafidz.com/artisan/${id}`,
          image: profile?.avatar_url ?? undefined,
          address: {
            "@type": "PostalAddress",
            addressLocality: artisan.commune,
            addressRegion: artisan.wilaya,
            addressCountry: "DZ",
          },
          knowsAbout: artisan.specialite,
        }
      : { "@context": "https://schema.org", "@type": "Person" },
    `artisan-${id}`
  );

  const isHandyman = artisan?.specialite === "Artisan polyvalent";
  const { data: handymanSpecialties = [] } = useHandymanSpecialties(
    isHandyman ? artisan?.id : undefined
  );

  const handleContact = async () => {
    if (!user) {
      toast({ title: t("profil_public.toast_login_title"), description: t("profil_public.toast_login_desc") });
      lNavigate("/connexion");
      return;
    }
    if (!message.trim()) {
      toast({ title: t("profil_public.toast_empty_title"), description: t("profil_public.toast_empty_desc"), variant: "destructive" });
      return;
    }
    if (!artisan) return;

    setSending(true);
    try {
      const { data: existing } = await supabase
        .from("conversations")
        .select("id")
        .eq("client_id", user.id)
        .eq("artisan_id", artisan.user_id)
        .maybeSingle();

      let conversationId = existing?.id;

      if (!conversationId) {
        const { data: newConvo, error: convoErr } = await supabase
          .from("conversations")
          .insert({ client_id: user.id, artisan_id: artisan.user_id })
          .select("id")
          .single();
        if (convoErr) throw convoErr;
        conversationId = newConvo.id;
      }

      const { error: msgErr } = await supabase
        .from("messages")
        .insert({ conversation_id: conversationId, sender_id: user.id, content: message.trim() });
      if (msgErr) throw msgErr;

      toast({ title: t("profil_public.toast_sent_title"), description: t("profil_public.toast_sent_desc") });
      setMessage("");
      lNavigate("/messagerie");
    } catch (err: any) {
      toast({ title: t("profil_public.toast_error"), description: err.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="container max-w-4xl py-8 space-y-5">
          <Skeleton className="h-44 w-full rounded-2xl" />
          <div className="grid gap-5 lg:grid-cols-[1fr_300px]">
            <div className="space-y-4">
              <Skeleton className="h-20 w-full rounded-xl" />
              <Skeleton className="h-32 w-full rounded-xl" />
              <Skeleton className="h-48 w-full rounded-xl" />
            </div>
            <Skeleton className="h-64 w-full rounded-xl" />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!artisan) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <main className="container max-w-3xl py-16 text-center">
          <Wrench className="mx-auto h-16 w-16 text-muted-foreground/30" />
          <h1 className="mt-4 font-display text-2xl font-bold">{t("profil_public.not_found")}</h1>
          <p className="mt-2 text-muted-foreground">{t("profil_public.not_found_desc")}</p>
          <Button variant="outline" className="mt-6" onClick={() => navigate(-1)}>
            {t("profil_public.back_search")}
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  const locationText = [artisan.commune, artisan.wilaya].filter(Boolean).join(", ") || t("profil_public.location_unknown");
  const hasContactLinks = artisan.phone || artisan.website_url || artisan.facebook_url || artisan.instagram_url;
  const isDisponible = (artisan as any).disponibilite === "disponible";
  const firstName = displayName.split(" ")[0];
  const artisanColors = getCategoryColorsByLabel(artisan.specialite ?? "");

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="container max-w-4xl py-8 pb-16">

        {/* ── Hero header ── */}
        <div className="gradient-hero rounded-2xl overflow-hidden mb-6 shadow-warm-lg">
          <div className="p-6 md:p-8">
            <div className="flex items-start gap-5">
              <div className="flex h-[88px] w-[88px] shrink-0 items-center justify-center rounded-2xl bg-background/20 backdrop-blur-sm overflow-hidden ring-2 ring-white/25">
                {artisan.logo_url ? (
                  <img src={artisan.logo_url} alt={displayName} className="h-full w-full object-cover" />
                ) : (
                  <Wrench className="h-10 w-10 text-primary-foreground/80" />
                )}
              </div>

              <div className="flex-1 min-w-0 text-primary-foreground">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <h1 className="font-display text-2xl md:text-3xl font-bold leading-tight">
                        {displayName}
                      </h1>
                      <VerificationBadge
                        type={artisan.type}
                        verificationStatus={(artisan as any).verification_status}
                        className="border border-white/20"
                      />
                    </div>
                    {artisan.raison_sociale && displayName !== artisan.raison_sociale && (
                      <p className="mt-0.5 text-primary-foreground/75 text-sm">{artisan.raison_sociale}</p>
                    )}
                    <div className="mt-2 flex items-center gap-1.5 text-sm text-primary-foreground/75">
                      <MapPin className="h-3.5 w-3.5 shrink-0" />
                      <span>{locationText}</span>
                    </div>
                  </div>

                  {isDisponible && (
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-accent px-3 py-1 text-xs font-semibold text-accent-foreground shadow-sm">
                      <span className="h-1.5 w-1.5 rounded-full bg-accent-foreground animate-pulse" />
                      {t("profil_public.available")}
                    </span>
                  )}
                </div>

                {artisan.specialite && (
                  <div className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-white/10 px-3 py-1.5 text-sm font-medium text-primary-foreground backdrop-blur-sm border border-white/20">
                    <span className={cn("flex h-5 w-5 items-center justify-center rounded-md", artisanColors.iconBg)}>
                      <Wrench className={cn("h-3 w-3", artisanColors.iconText)} />
                    </span>
                    {artisan.specialite}
                  </div>
                )}

                {isHandyman && handymanSpecialties.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {handymanSpecialties.map((s) => (
                      <Badge
                        key={s.specialty_code}
                        variant="outline"
                        className="text-xs border-white/30 text-primary-foreground bg-white/10"
                      >
                        {getHandymanLabel(s.specialty_code as HandymanSpecialtyCode, s.other_specialty_label)}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Layout 2 colonnes ── */}
        <div className="grid gap-5 lg:grid-cols-[1fr_300px]">

          <div className="space-y-4">
            <Card className="shadow-warm-sm">
              <CardContent className="p-5 space-y-2.5">
                <ArtisanBadges
                  isReactiveBadge={artisan.is_reactive_badge ?? undefined}
                  profileCompletionScore={artisan.profile_completion_score ?? undefined}
                />
                <ArtisanSignals
                  responseRate={artisan.response_rate ? Number(artisan.response_rate) : undefined}
                  avgResponseTimeMinutes={artisan.avg_response_time_minutes ? Number(artisan.avg_response_time_minutes) : undefined}
                  lastLoginAt={artisan.last_login_at ?? undefined}
                />
              </CardContent>
            </Card>

            {artisan.description && (
              <Card className="shadow-warm-sm">
                <CardContent className="p-5">
                  <h2 className="mb-3 font-display text-lg font-semibold">{t("profil_public.about")}</h2>
                  <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                    {artisan.description}
                  </p>
                </CardContent>
              </Card>
            )}

            {artisan.communes_couvertes && artisan.communes_couvertes.length > 0 && (
              <Card className="shadow-warm-sm">
                <CardContent className="p-5">
                  <h2 className="mb-3 font-display text-lg font-semibold">{t("profil_public.zones")}</h2>
                  <div className="flex flex-wrap gap-2">
                    {artisan.communes_couvertes.map((c) => (
                      <Badge key={c} variant="outline" className="text-sm gap-1">
                        <MapPin className="h-3 w-3" />{c}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {id && (
              <Card className="shadow-warm-sm">
                <CardContent className="p-5">
                  <PortfolioGallery userId={id} />
                </CardContent>
              </Card>
            )}
          </div>

          {/* ── Colonne contact ── */}
          <div className="space-y-3">
            <Card className={cn("shadow-warm-md lg:sticky lg:top-20 border-t-4", artisanColors.borderTop)}>
              <CardContent className="p-5 space-y-4">
                <div>
                  <h2 className="font-display text-lg font-semibold">
                    {t("profil_public.contact_title", { name: firstName })}
                  </h2>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {t("profil_public.contact_hint")}
                  </p>
                </div>

                <Textarea
                  placeholder={t("profil_public.contact_placeholder")}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="min-h-[120px] resize-none text-sm"
                />

                <Button
                  variant="hero"
                  className="w-full gap-2"
                  onClick={handleContact}
                  disabled={sending || !message.trim()}
                >
                  <Send className="h-4 w-4" />
                  {sending ? t("profil_public.sending") : t("profil_public.send")}
                </Button>

                {hasContactLinks && (
                  <>
                    <div className="flex items-center gap-2">
                      <div className="h-px flex-1 bg-border" />
                      <span className="text-xs text-muted-foreground px-1">{t("profil_public.or")}</span>
                      <div className="h-px flex-1 bg-border" />
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {artisan.phone && (
                        user ? (
                          <a href={`tel:${artisan.phone}`} className="flex-1 min-w-[120px]">
                            <Button variant="outline" size="sm" className="w-full gap-1.5">
                              <Phone className="h-3.5 w-3.5" />
                              {artisan.phone}
                            </Button>
                          </a>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="flex-1 min-w-[120px] gap-1.5 relative overflow-hidden group"
                            onClick={() => lNavigate("/connexion")}
                            title={t("profil_public.phone_locked_label")}
                          >
                            <Phone className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                            <span className="blur-sm select-none pointer-events-none flex-1 text-start text-foreground">
                              0555 •• •• ••
                            </span>
                            <Lock className="h-3 w-3 shrink-0 text-muted-foreground" />
                          </Button>
                        )
                      )}
                      {artisan.website_url && (
                        <a href={artisan.website_url} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" size="sm" className="gap-1.5">
                            <Globe className="h-3.5 w-3.5" />
                            {t("profil_public.website")}
                          </Button>
                        </a>
                      )}
                      {artisan.facebook_url && (
                        <a href={artisan.facebook_url} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" size="sm" className="gap-1.5">
                            <Facebook className="h-3.5 w-3.5" />
                            Facebook
                          </Button>
                        </a>
                      )}
                      {artisan.instagram_url && (
                        <a href={artisan.instagram_url} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" size="sm" className="gap-1.5">
                            <Instagram className="h-3.5 w-3.5" />
                            Instagram
                          </Button>
                        </a>
                      )}
                    </div>
                  </>
                )}

                {!user && artisan.phone && (
                  <p className="text-xs text-center text-muted-foreground border-t pt-3">
                    <button
                      onClick={() => lNavigate("/connexion")}
                      className="text-primary font-semibold hover:underline"
                    >
                      {t("profil_public.phone_login_link")}
                    </button>
                    {" "}{t("profil_public.phone_login_suffix")}
                  </p>
                )}
              </CardContent>
            </Card>

            {user && user.id !== id && (
              <button
                onClick={() => setReportOpen(true)}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors w-full justify-center py-1"
              >
                <Flag className="h-3 w-3" />
                {t("profil_public.report")}
              </button>
            )}
          </div>

        </div>
      </main>
      <Footer />

      {id && (
        <ReportModal
          open={reportOpen}
          onClose={() => setReportOpen(false)}
          targetType="user"
          targetId={id}
          targetName={displayName}
        />
      )}
    </div>
  );
};

export default ProfilArtisan;
